import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit";
import { getAllMeetings, editMeeting, deleteMeeting } from "@/api/misc";
import { type AppState } from "..";
import { type Meeting } from "@/types/meeting";

interface MeetingsState {
  allMeetings: Meeting[];
  isLoading: boolean;
  error: string | null;
}

const initialState: MeetingsState = {
  allMeetings: [],
  isLoading: false,
  error: null,
};

// Async thunk for getting meetings
export const getMeetingsAction = createAsyncThunk(
  "meeting/getMeetings",
  async (organizationData: { id: string }, { rejectWithValue }) => {
    try {
      const response = await getAllMeetings(organizationData.id);
      return response.meetings || response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Async thunk for deleting a meeting
export const deleteMeetingAction = createAsyncThunk(
  "meeting/deleteMeeting",
  async (data: { organizationId: string; meetingId: string }, { rejectWithValue }) => {
    try {
      await deleteMeeting({
        organizationId: data.organizationId,
        meetingId: data.meetingId,
      });

      const response = await getAllMeetings(data.organizationId);
      return response.meetings || response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// Async thunk for editing a meeting
export const editMeetingAction = createAsyncThunk(
  "meeting/editMeeting",
  async (
    {
      organizationId,
      meetingId,
      updatedData,
    }: { organizationId: string; meetingId: string; updatedData: Partial<Meeting> },
    { getState },
  ) => {
    await editMeeting({ organizationId, meetingId }, updatedData);
    // If no error was thrown, the edit was successful
    return (getState() as AppState).meeting.allMeetings.map((item) =>
      item.id === meetingId ? { ...item, ...updatedData } : item,
    );
  },
);

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMeetingsAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMeetingsAction.fulfilled, (state, action: PayloadAction<Meeting[]>) => {
        state.isLoading = false;
        state.allMeetings = action.payload;
      })
      .addCase(getMeetingsAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as Error)?.message ?? "Failed to fetch meetings";
        console.error("getMeetingsAction rejected:", action.payload);
      })
      .addCase(deleteMeetingAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteMeetingAction.fulfilled, (state, action: PayloadAction<Meeting[]>) => {
        state.isLoading = false;
        state.allMeetings = action.payload;
      })
      .addCase(deleteMeetingAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as Error)?.message ?? "Failed to delete meeting";
        console.error("deleteMeetingAction rejected:", action.payload);
      })
      .addCase(editMeetingAction.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editMeetingAction.fulfilled, (state, action: PayloadAction<Meeting[]>) => {
        state.isLoading = false;
        state.allMeetings = action.payload;
      })
      .addCase(editMeetingAction.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as Error)?.message ?? "Failed to edit meeting";
        console.error("editMeetingAction rejected:", action.payload);
      });
  },
});

export default meetingSlice.reducer;
